import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { selectConnection } from 'store/core/selectors';
import { selectContexts } from 'store/graph/selectors';
import { formatVertexSearchResponse } from 'utils/formatters/search-formatter';
import styles from './command-bar.module.css';

const ICON_MAP = {
  person: '👤',
  org: '🏢',
  fund: '💰',
  education: '🎓',
  engagement: '💼',
  region: '📍',
};

const ICON_COLOR_CLASS = {
  person: 'resultIconPerson',
  org: 'resultIconOrg',
  fund: 'resultIconFund',
  education: 'resultIconEducation',
  engagement: 'resultIconEngagement',
  region: 'resultIconRegion',
};

const GROUP_ORDER = ['person', 'org', 'fund', 'education', 'engagement', 'region'];
const GROUP_LABELS = {
  person: 'People',
  org: 'Organizations',
  fund: 'Funds',
  education: 'Education',
  engagement: 'Engagements',
  region: 'Regions',
};

const normalizeGeoResult = (geoItem) => ({
  id: '',
  displayValue: geoItem.value,
  label: 'region',
  type: 'region',
  metroRegion: geoItem.value,
  geoLevel: geoItem.type,
  _rawDisplay: geoItem.display,
});

const parseSearchResponse = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (response.tables) return formatVertexSearchResponse(response);
  return [];
};

const parseVertexSearchResponse = (response) =>
  parseSearchResponse(response).map((item) => ({
    ...item,
    displayValue: item.displayValue || item.name || item.value || item.id,
  }));

const parseGeoSearchResponse = (response) =>
  parseSearchResponse(response)
    .map((item) => ({
      ...item,
      value: item.value || item.metroRegion || item.displayValue || item.name,
    }))
    .filter((item) => item && item.value);

const groupResults = (items) => {
  const groups = {};
  items.forEach((item) => {
    const key = item.label || 'person';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return groups;
};

const AutocompleteDropdown = forwardRef(({ searchTerm, onSelect, selectedContextIds }, ref) => {
  const connection = useSelector(selectConnection);
  const graphContexts = useSelector(selectContexts);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  const toggleGroup = useCallback((groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey);
      return next;
    });
  }, []);

  // Group results once, then derive flat list for keyboard navigation
  const grouped = React.useMemo(() => groupResults(results), [results]);
  const flatResults = React.useMemo(() => {
    const flat = [];
    GROUP_ORDER.forEach((key) => {
      if (grouped[key] && !collapsedGroups.has(key)) flat.push(...grouped[key]);
    });
    return flat;
  }, [grouped, collapsedGroups]);

  const doSearch = useCallback(
    async (term) => {
      if (!connection || !term || term.length < 1) {
        setResults([]);
        return;
      }
      setIsLoading(true);
      try {
        const [nodeResponse, geoResponse] = await Promise.all([
          connection.invoke('filterSearch', term, 'vertex').catch(() => null),
          connection.invoke('geoSearch', term).catch(() => null),
        ]);

        const MAX_TOTAL = 50;
        const MAX_GEO = 10;
        const MAX_PER_GROUP = 10;

        const nodeResults = parseVertexSearchResponse(nodeResponse);
        const geoResults = parseGeoSearchResponse(geoResponse);
        const geoNormalized = geoResults.map(normalizeGeoResult);

        // Reserve slots for geo results so they aren't crowded out
        const geoCount = Math.min(geoNormalized.length, MAX_GEO);
        const vertexBudget = MAX_TOTAL - geoCount;

        // Group vertex results by label
        const vertexItems = nodeResults.map((item) => ({
          ...item,
          type: 'vertex',
          label: item.label || 'person',
        }));
        const vertexGrouped = {};
        vertexItems.forEach((item) => {
          const key = item.label;
          if (!vertexGrouped[key]) vertexGrouped[key] = [];
          vertexGrouped[key].push(item);
        });

        // Fair-share allocation: groups under cap donate surplus to others
        const groupKeys = Object.keys(vertexGrouped);
        const allocations = {};
        let remaining = vertexBudget;
        let unresolved = [...groupKeys];

        while (unresolved.length > 0 && remaining > 0) {
          const fairShare = Math.floor(remaining / unresolved.length);
          const nextUnresolved = [];
          let used = 0;
          for (const key of unresolved) {
            const available = vertexGrouped[key].length - (allocations[key] || 0);
            const cap = Math.min(MAX_PER_GROUP - (allocations[key] || 0), fairShare);
            const take = Math.min(available, cap);
            allocations[key] = (allocations[key] || 0) + take;
            used += take;
            if (available > take && allocations[key] < MAX_PER_GROUP) {
              nextUnresolved.push(key);
            }
          }
          remaining -= used;
          if (used === 0) break;
          unresolved = nextUnresolved;
        }

        let merged = [];
        groupKeys.forEach((key) => {
          merged.push(...vertexGrouped[key].slice(0, allocations[key] || 0));
        });

        // Append geo results (guaranteed slots)
        merged.push(...geoNormalized.slice(0, MAX_GEO));

        // Filter out already-selected contexts
        merged = merged.filter(
          (item) =>
            !selectedContextIds.has(item.id || `region-${item.metroRegion}`),
        );

        setResults(merged.slice(0, MAX_TOTAL));
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [connection, selectedContextIds],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchTerm) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(searchTerm), 500);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm, doSearch]);

  // Reset highlight and expand all groups when results change
  useEffect(() => {
    setHighlightIndex(0);
    setCollapsedGroups(new Set());
  }, [results]);

  // Keyboard handler — called from parent via ref
  const handleKeyDown = useCallback(
    (e) => {
      const total = flatResults.length;
      if (total === 0) return false;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => (prev + 1) % total);
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => (prev === 0 ? total - 1 : prev - 1));
        return true;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (flatResults[highlightIndex]) onSelect(flatResults[highlightIndex]);
        return true;
      }
      return false;
    },
    [flatResults, highlightIndex, onSelect],
  );

  // Expose handleKeyDown to parent via ref
  useImperativeHandle(ref, () => ({ handleKeyDown }), [handleKeyDown]);

  // Scroll highlighted item into view
  useEffect(() => {
    const el = dropdownRef.current?.querySelector(`[data-index="${highlightIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const graphPicks = graphContexts.filter(
    (ctx) => ctx.id && !selectedContextIds.has(ctx.id),
  );

  const showDropdown = searchTerm || graphPicks.length > 0;

  if (!showDropdown) return null;

  return (
    <div className={styles.autocompleteWrapper} ref={dropdownRef}>
      <div className={styles.autocompleteDropdown}>
        {/* "From your graph" section */}
        {graphPicks.length > 0 && (
          <div className={styles.graphPicksSection}>
            <div className={styles.sectionLabel}>From your graph</div>
            <div className={styles.graphPicks}>
              {graphPicks.slice(0, 10).map((ctx) => {
                const display = ctx.displayValue || ctx.name || ctx.id;
                return (
                <button
                  key={ctx.id}
                  className={styles.graphPickChip}
                  onClick={() =>
                    onSelect({
                      ...ctx,
                      type: 'vertex',
                      label: ctx.label || 'person',
                      value: display,
                      displayValue: display,
                    })
                  }
                >
                  {ICON_MAP[ctx.label] || '👤'} {display}
                </button>);
              })
              }
            </div>
          </div>
        )}

        {/* Loading */}
        {isLoading && <div className={styles.loadingRow}>Searching…</div>}

        {/* No results */}
        {!isLoading && searchTerm && results.length === 0 && (
          <div className={styles.noResults}>No results for "{searchTerm}"</div>
        )}

        {/* Grouped results */}
        {!isLoading &&
          GROUP_ORDER.map((groupKey) => {
            const items = grouped[groupKey];
            if (!items || items.length === 0) return null;
            return (
              <div key={groupKey} className={styles.resultGroup}>
                <div
                  className={`${styles.sectionLabel} ${styles.collapsibleHeader}`}
                  onClick={() => toggleGroup(groupKey)}
                  role="button"
                  aria-expanded={!collapsedGroups.has(groupKey)}
                >
                  <span>{ICON_MAP[groupKey]} {GROUP_LABELS[groupKey]} ({items.length})</span>
                  <span className={styles.collapseChevron}>
                    {collapsedGroups.has(groupKey) ? '▸' : '▾'}
                  </span>
                </div>
                {!collapsedGroups.has(groupKey) && items.map((item) => {
                  const idx = flatResults.indexOf(item);
                  return (
                    <div
                      key={item.id || `${item.metroRegion}-${idx}`}
                      data-index={idx}
                      className={`${styles.resultItem} ${
                        idx === highlightIndex ? styles.resultItemHighlighted : ''
                      }`}
                      onClick={() => onSelect(item)}
                      onMouseEnter={() => setHighlightIndex(idx)}
                    >
                      <span className={`${styles.resultIcon} ${styles[ICON_COLOR_CLASS[item.label]] || ''}`}>
                        {ICON_MAP[item.label] || '👤'}
                      </span>
                      <span className={styles.resultText}>
                        {item.displayValue || item.metroRegion}
                      </span>
                      {item.metroRegion && item.type !== 'region' && (
                        <span className={styles.resultMeta}>
                          {item.metroRegion}
                        </span>
                      )}
                      {item.geoLevel && (
                        <span className={styles.resultMeta}>
                          {item.geoLevel}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
      </div>
    </div>
  );
});

AutocompleteDropdown.displayName = 'AutocompleteDropdown';

export default AutocompleteDropdown;
export { ICON_MAP };
